import React, { useCallback, useEffect, useState } from "react";
import { Button, Table, Switch, Tooltip, Modal, Input, message } from "antd";
import { KeyOutlined, ReloadOutlined } from "@ant-design/icons";
import * as api from "./controller";
import "./crm.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;
const errmsg = (r, f) => (r && r.json && r.json.detail) || f;
const ELLIPSIS = { display: "inline-block", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" };

/** Super-admin per-practice user access matrix, embedded in the Practice detail drawer.
 * Manage which offices each user can access (grant/revoke), plus deactivate + password. */
export default function PracticeUsersPanel({ practiceId }) {
  const [offices, setOffices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [pwUser, setPwUser] = useState(null);

  const apply = (json) => { if (json) { setOffices(json.offices || []); setUsers(json.users || []); } };

  const load = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    try {
      const r = await api.getPracticeUsers(practiceId);
      if (ok(r)) apply(r.json);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);
  useEffect(() => { load(); }, [load]);

  const toggleOffice = async (user, office, grant) => {
    setBusy(`${user.id}:${office.id}`);
    const r = await api.setUserOfficeAccess(practiceId, user.id, office.id, grant);
    setBusy("");
    if (ok(r)) apply(r.json);
    else message.error(errmsg(r, "Couldn't change access."));
  };
  const toggleActive = async (user, active) => {
    const r = await api.setUserActive(user.id, active);
    if (ok(r)) { message.success(active ? "Reactivated." : "Deactivated."); load(); }
    else message.error(errmsg(r, "Couldn't update the account."));
  };

  const officeCols = offices.map((o) => ({
    title: <Tooltip title={o.name}><span style={ELLIPSIS}>{o.name}</span></Tooltip>,
    key: `o${o.id}`, width: 92, align: "center",
    render: (_, u) => {
      const cell = (u.offices || []).find((x) => x.id === o.id) || {};
      // Owner access (has_access but not removable via our role) can't be revoked here.
      const lockedOn = cell.has_access && !cell.removable;
      return (
        <Tooltip title={lockedOn ? "Owner access (managed via their role)" : (cell.has_access ? "Has access" : "No access")}>
          <Switch size="small" checked={!!cell.has_access} disabled={lockedOn}
            loading={busy === `${u.id}:${o.id}`}
            onChange={(v) => toggleOffice(u, o, v)} />
        </Tooltip>
      );
    },
  }));

  const columns = [
    {
      title: "User", fixed: "left", width: 200,
      render: (_, u) => (
        <div className="crm-cell-stack">
          <div className="crm-name">{u.name || u.email}</div>
          <div className="crm-sub">{u.email}</div>
        </div>
      ),
    },
    {
      title: "Active", width: 76, align: "center", fixed: "left",
      render: (_, u) => <Switch size="small" checked={u.is_active} onChange={(v) => toggleActive(u, v)} />,
    },
    ...officeCols,
    {
      title: "", key: "a", width: 56, align: "right",
      render: (_, u) => <Button size="small" icon={<KeyOutlined />} onClick={() => setPwUser(u)} />,
    },
  ];

  return (
    <div>
      <div className="crm-row-actions" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <span className="crm-sub">Toggle which offices each user can access. Add or invite users from an office's detail view.</span>
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
      </div>
      <Table size="small" rowKey="id" columns={columns} dataSource={users} loading={loading}
        pagination={false} scroll={{ x: "max-content" }}
        locale={{ emptyText: <div className="crm-empty">No users under this practice yet.</div> }} />

      <SetPwModal user={pwUser} onClose={() => setPwUser(null)} onDone={() => setPwUser(null)} />
    </div>
  );
}

function SetPwModal({ user, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPassword(""); }, [user]);

  const submit = async () => {
    if (!password) { message.error("Enter a new password."); return; }
    // set-password is office-scoped; use any office the user belongs to (superuser is
    // authorized for all, and the target is a member of that office).
    const office = (user.offices || []).find((o) => o.has_access);
    if (!office) { message.error("User isn't in any office of this practice."); return; }
    setBusy(true);
    const r = await api.setOfficeUserPassword(office.id, user.id, password);
    setBusy(false);
    if (ok(r)) { message.success("Password updated."); onDone(); }
    else message.error(errmsg(r, "Could not update the password."));
  };

  return (
    <Modal title={user ? `Set password — ${user.email}` : "Set password"} open={!!user}
      onCancel={onClose} onOk={submit} okText="Update password" confirmLoading={busy}
      zIndex={10050} destroyOnClose>
      <label className="crm-field-label">New password</label>
      <Input.Password autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
    </Modal>
  );
}
